// Registry dei controlli renderizzabili in un nodo di flow.
//
// Perché serve un livello di adapter e non basta `<component :is>`:
// i componenti CoreUI NON hanno lo stesso contratto v-model.
//   CFormInput / CFormTextarea / ... → prop `modelValue`, evento `update:modelValue`
//   CDatePicker                      → prop `date`,       evento `update:date`
//   CMultiSelect                     → prop `value`,      evento `change` con
//                                      payload = array di OPZIONI (non di valori)
// Qui ogni controllo dichiara il proprio contratto, così `FlowFormStep.vue`
// resta un renderer unico e generico.
//
// Il registry è anche la WHITELIST: un flow può usare solo i nomi presenti qui,
// e di ogni controllo solo le props elencate in `allowedProps`. Aggiungere un
// controllo di dominio (es. un componente che carica foto) si fa con
// `registerFlowControl()` da codice TS, non dal JSON.

import { markRaw, type Component } from 'vue'
import {
  CAutocomplete, CDatePicker, CFormCheck, CFormInput, CFormRange, CFormSelect,
  CFormSwitch, CFormTextarea, CMultiSelect, CRating,
} from '@coreui/vue-pro'
import type { FlowOption } from './flowTypes'

export type FlowControlKind = 'single' | 'radio-group' | 'button-group' | 'display'

export interface FlowControlDefinition {
  name: string
  label: string
  /** Come il renderer disegna il controllo. */
  kind: FlowControlKind
  /** Componente da montare (assente per `radio-group`/`button-group`/`display`). */
  component?: Component
  /** Nome della prop che porta il valore. Default `modelValue`. */
  modelProp?: string
  /** Nome dell'evento che aggiorna il valore. Default `update:modelValue`. */
  modelEvent?: string
  /** Normalizza il payload dell'evento nel valore da salvare nella risposta. */
  fromEvent?: (payload: unknown, props: Record<string, unknown>) => unknown
  /** Adatta il valore salvato al formato atteso dalla prop. */
  toProp?: (value: unknown) => unknown
  /** Prop su cui passare le opzioni, se il controllo le accetta. */
  optionsProp?: string
  /** Il controllo non è utilizzabile senza `options` / `optionsFrom`. */
  needsOptions?: boolean
  /**
   * Il controllo ha bisogno di una voce vuota iniziale. Serve a `<select>`:
   * senza di essa il browser preseleziona la prima opzione e il campo risulta
   * "risposto" senza che l'utente abbia scelto nulla.
   */
  placeholderOption?: boolean
  /** Props che un flow può impostare. Tutto il resto viene scartato. */
  allowedProps?: string[]
  /**
   * Valori proposti nel builder per una prop, chiave = nome della prop.
   * NON è una whitelist: la casella resta libera, perché il valore può essere
   * un template (`{{item.numero}}`). Serve a non far digitare a memoria nomi
   * che devono combaciare con dati esistenti — un tag sbagliato non fallisce,
   * archivia le foto dove nessuno le cerca.
   *
   * È una funzione e non un array perché i valori arrivano dalle impostazioni,
   * che al momento della registrazione del controllo non sono ancora caricate.
   */
  propOptions?: Record<string, () => FlowOption[]>
  /** Props sempre applicate (prima di quelle del flow). */
  defaultProps?: Record<string, unknown>
  /**
   * Valore iniziale quando il campo non ne dichiara uno.
   * Se è un array o un oggetto, leggilo SEMPRE con `emptyValueOf()`: preso
   * direttamente sarebbe la stessa istanza condivisa da ogni campo che usa
   * questo controllo, in ogni nodo e per tutta la vita della pagina.
   */
  emptyValue?: unknown
  /**
   * Il controllo dichiara `modelValue` con `Boolean` PRIMA di `String`
   * (es. CFormCheck: `[Array, Boolean, String]`). In quel caso Vue applica il
   * boolean-casting e converte la STRINGA VUOTA in `true` — con effetti
   * disastrosi: un radio group mostrerebbe tutte le opzioni come `checked` e
   * smetterebbe di emettere. Per questi controlli il renderer sostituisce `''`
   * con `undefined`.
   */
  booleanCast?: boolean
  /** Ulteriore sanificazione dei valori delle props (es. `type` di CFormInput). */
  sanitize?: (props: Record<string, unknown>) => Record<string, unknown>
}

const registry = new Map<string, FlowControlDefinition>()

export function registerFlowControl(definition: FlowControlDefinition): void {
  if (registry.has(definition.name)) {
    console.debug('[flow/controls] controllo ridefinito:', definition.name)
  }
  registry.set(definition.name, {
    ...definition,
    component: definition.component ? markRaw(definition.component) : undefined,
  })
}

export function registerFlowControls(definitions: FlowControlDefinition[]): void {
  definitions.forEach(registerFlowControl)
}

export function getFlowControl(name: string): FlowControlDefinition | undefined {
  return registry.get(name)
}

export function hasFlowControl(name: string): boolean {
  return registry.has(name)
}

/** Elenco per il builder: la tavolozza di controlli disponibili. */
export function listFlowControls(): FlowControlDefinition[] {
  return [...registry.values()].sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Valore iniziale del controllo, sempre in una copia FRESCA.
 *
 * `emptyValue` vive nel registry, che è un singleton: restituire l'array
 * dichiarato lì significherebbe consegnare la STESSA istanza a ogni campo che
 * usa il controllo — due campi foto nello stesso wizard condividerebbero
 * l'elenco, e le foto resterebbero lì anche dopo un `start()`.
 */
export function emptyValueOf(control: FlowControlDefinition): unknown {
  const empty = control.emptyValue
  if (Array.isArray(empty)) return [...empty]
  if (empty !== null && typeof empty === 'object') return { ...empty }
  return empty ?? null
}

/** Filtra le props di un campo sulla whitelist del controllo. */
export function sanitizeControlProps(
  control: FlowControlDefinition,
  props: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const allowed = control.allowedProps ?? []
  const out: Record<string, unknown> = { ...(control.defaultProps ?? {}) }

  for (const [key, value] of Object.entries(props ?? {})) {
    if (!allowed.includes(key)) {
      console.warn(`[flow/controls] prop '${key}' non ammessa su '${control.name}': ignorata`)
      continue
    }
    out[key] = value
  }

  return control.sanitize ? control.sanitize(out) : out
}

/**
 * Normalizza `options` / risultato di `optionsFrom` in `FlowOption[]`.
 *
 * Le colonne che non riconosce NON si perdono: restano sull'opzione, dove
 * `optionsWhere` può leggerle. Sono la sola via per filtrare una tendina su un
 * attributo della riga (la sede di un'officina, il reparto di uno stato) invece
 * che sul solo valore.
 */
export function normalizeOptions(
  raw: FlowOption[] | Record<string, string> | unknown,
): FlowOption[] {
  if (!raw) return []

  if (Array.isArray(raw)) {
    return raw
      .map((o): FlowOption | null => {
        if (o === null || typeof o !== 'object') {
          return { value: o, label: String(o) }
        }
        const rec = o as Record<string, unknown>
        // Accetta {value,label} ma anche le forme già usate nelle impostazioni.
        const value = 'value' in rec ? rec.value : rec.id ?? rec.codice
        const label = rec.label ?? rec.text ?? rec.descrizione ?? rec.nome ?? value
        if (value === undefined) return null
        return {
          // La riga di origine passa INTERA: `optionsWhere` deve poter filtrare
          // su colonne che non sono né `value` né `label` (`option.sede_id`).
          // Le chiavi normalizzate vengono dopo, così vincono sempre loro.
          ...rec,
          value,
          label: String(label ?? ''),
          description: typeof rec.description === 'string' ? rec.description : undefined,
          icon: typeof rec.icon === 'string' ? rec.icon : undefined,
          color: typeof rec.color === 'string' ? rec.color : undefined,
        }
      })
      .filter((o): o is FlowOption => o !== null)
  }

  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, string>).map(([value, label]) => ({
      value,
      label: String(label),
    }))
  }

  return []
}

// ─── Controlli di base (CoreUI) ───────────────────────────────────────────────

/** `type` ammessi su CFormInput: esclusi file/password/hidden. */
const SAFE_INPUT_TYPES = new Set([
  'text', 'number', 'date', 'time', 'datetime-local', 'month', 'week',
  'email', 'tel', 'url', 'search', 'color',
])

registerFlowControls([
  {
    name: 'CFormInput',
    label: 'Campo di testo / numero',
    kind: 'single',
    component: CFormInput,
    emptyValue: '',
    allowedProps: [
      'type', 'placeholder', 'min', 'max', 'step', 'maxlength', 'minlength',
      'disabled', 'readonly', 'size', 'plaintext', 'floatingLabel', 'autocomplete',
    ],
    defaultProps: { type: 'text' },
    // `type="number"` restituisce comunque una stringa dal DOM: la si converte
    // qui, così un flow può scrivere la risposta in un campo numerico senza che
    // ogni azione debba ricordarsi di fare `Number()`.
    fromEvent: (payload, props) => {
      if (props.type !== 'number') return payload
      if (payload === '' || payload === null || payload === undefined) return null
      const n = Number(payload)
      return Number.isFinite(n) ? n : null
    },
    sanitize: (props) => {
      const type = String(props.type ?? 'text')
      if (!SAFE_INPUT_TYPES.has(type)) {
        console.warn(`[flow/controls] type '${type}' non ammesso su CFormInput: uso 'text'`)
        return { ...props, type: 'text' }
      }
      return props
    },
  },
  {
    name: 'CFormTextarea',
    label: 'Testo su più righe',
    kind: 'single',
    component: CFormTextarea,
    emptyValue: '',
    allowedProps: ['rows', 'placeholder', 'disabled', 'readonly', 'maxlength', 'floatingLabel'],
    defaultProps: { rows: 4 },
  },
  {
    name: 'CFormSelect',
    label: 'Menu a tendina',
    kind: 'single',
    component: CFormSelect,
    optionsProp: 'options',
    needsOptions: true,
    placeholderOption: true,
    emptyValue: '',
    allowedProps: ['disabled', 'size', 'floatingLabel', 'placeholder'],
  },
  {
    name: 'CFormCheck',
    label: 'Casella di spunta (sì/no)',
    kind: 'single',
    component: CFormCheck,
    emptyValue: false,
    booleanCast: true,
    allowedProps: ['label', 'inline', 'disabled', 'reverse', 'indeterminate'],
  },
  {
    name: 'CFormSwitch',
    label: 'Interruttore (sì/no)',
    kind: 'single',
    component: CFormSwitch,
    emptyValue: false,
    // modelValue: [Boolean, String] → stessa trappola di CFormCheck.
    booleanCast: true,
    allowedProps: ['label', 'disabled', 'size', 'reverse'],
  },
  {
    name: 'CFormRange',
    label: 'Cursore numerico',
    kind: 'single',
    component: CFormRange,
    emptyValue: 0,
    allowedProps: ['min', 'max', 'step', 'disabled'],
    fromEvent: (payload) => {
      const n = Number(payload)
      return Number.isFinite(n) ? n : 0
    },
  },
  {
    name: 'CDatePicker',
    label: 'Data',
    kind: 'single',
    component: CDatePicker,
    // Contratto diverso dagli altri: prop `date`, evento `update:date`.
    modelProp: 'date',
    modelEvent: 'update:date',
    emptyValue: null,
    allowedProps: [
      'locale', 'firstDayOfWeek', 'minDate', 'maxDate', 'placeholder', 'cleaner',
      'size', 'selectionType', 'navigation', 'disabled', 'timepicker', 'todayButton',
    ],
    defaultProps: { locale: 'it-IT', cleaner: true, placeholder: 'Seleziona una data' },
    // Salva sempre una stringa: il JSON delle risposte deve restare serializzabile.
    fromEvent: (payload, props) => {
      if (payload === null || payload === undefined || payload === '') return null
      const d = payload instanceof Date ? payload : new Date(String(payload))
      if (Number.isNaN(d.getTime())) return null
      const p = (n: number) => String(n).padStart(2, '0')
      const day = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
      return props.timepicker
        ? `${day} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
        : day
    },
  },
  {
    name: 'CMultiSelect',
    label: 'Selezione multipla',
    kind: 'single',
    component: CMultiSelect,
    optionsProp: 'options',
    needsOptions: true,
    // Contratto diverso: prop `value`, evento `change` con payload = opzioni.
    modelProp: 'value',
    modelEvent: 'change',
    emptyValue: [],
    allowedProps: [
      'multiple', 'placeholder', 'disabled', 'selectionType', 'optionsMaxHeight',
      'searchNoResultsLabel', 'selectAll', 'selectionLimit', 'search', 'cleaner', 'size',
    ],
    defaultProps: { multiple: true, placeholder: 'Seleziona...' },
    fromEvent: (payload) => {
      if (!Array.isArray(payload)) return []
      return payload.map((o) =>
        o !== null && typeof o === 'object' && 'value' in (o as Record<string, unknown>)
          ? (o as Record<string, unknown>).value
          : o,
      )
    },
    toProp: (value) => (Array.isArray(value) ? value : value === null || value === undefined ? [] : [value]),
  },
  {
    name: 'CAutocomplete',
    label: 'Ricerca con suggerimenti',
    kind: 'single',
    component: CAutocomplete,
    optionsProp: 'options',
    needsOptions: true,
    emptyValue: '',
    allowedProps: ['placeholder', 'disabled', 'cleaner', 'size', 'allowOnlyDefinedOptions'],
  },
  {
    name: 'CRating',
    label: 'Valutazione a stelle',
    kind: 'single',
    component: CRating,
    emptyValue: 0,
    allowedProps: ['max', 'allowClear', 'disabled', 'precision', 'size', 'readOnly'],
  },
  {
    name: 'radio',
    label: 'Scelta singola (radio)',
    // Un radio group non è un solo componente: sono N CFormCheck type="radio".
    // ATTENZIONE: CFormCheck emette `update:modelValue` solo se il modelValue
    // ricevuto è `undefined` o una STRINGA (vedi CFormCheck.js). Con valori
    // numerici il gruppo si bloccherebbe dopo la prima scelta: per questo
    // `FlowFormStep` lo pilota con l'INDICE dell'opzione come stringa e
    // rimappa al valore tipato in uscita.
    kind: 'radio-group',
    needsOptions: true,
    emptyValue: null,
    allowedProps: ['inline', 'disabled'],
  },
  {
    name: 'buttons',
    label: 'Scelta con tasti grandi',
    // Bottoni-scelta (CButton), lo schema usato oggi da SceltaAppuntamentoStep:
    // ogni opzione può avere icona, colore e testo descrittivo.
    kind: 'button-group',
    needsOptions: true,
    emptyValue: null,
    allowedProps: ['disabled', 'vertical'],
  },
  {
    name: 'text',
    label: 'Testo informativo',
    // Non produce risposta: serve a spiegare qualcosa dentro un nodo.
    kind: 'display',
    allowedProps: ['color', 'strong'],
  },
])
